import { Component, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { SessionService } from '@core/services/session';
import { AuthService } from '@core/services/auth';
import { DomainService } from '@core/services/domain';

@Component({
  selector: 'app-session-timeout',
  imports: [],
  templateUrl: './session-timeout.html',
  styleUrl: './session-timeout.scss',
})
export class SessionTimeout {
  readonly currentYear = new Date().getFullYear();

  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private sessionService = inject(SessionService);
  private authService = inject(AuthService);
  private domainService = inject(DomainService);

  private buildAdminReportParams(studentId: number): string {
    const payload = { studentId, ReqFrom: 'admin' };
    // Router queryParams handles URL encoding; keep this as raw base64.
    return btoa(JSON.stringify(payload));
  }

  private getAdminContextFromReportParams(
    reportparams: string | null,
  ): { reqFrom: string; studentId: number } | null {
    if (!reportparams) {
      return null;
    }

    try {
      const decoded = atob(decodeURIComponent(reportparams));
      const parsed = JSON.parse(decoded) as {
        studentId?: string | number;
        StudentId?: string | number;
        id?: string | number;
        Id?: string | number;
        ReqFrom?: string;
        reqFrom?: string;
      };

      const rawStudentId =
        parsed?.studentId ?? parsed?.StudentId ?? parsed?.id ?? parsed?.Id ?? null;
      const studentIdMatch = String(rawStudentId ?? '').match(/\d+/);
      const studentId = studentIdMatch ? Number(studentIdMatch[0]) : NaN;
      const reqFrom = String(parsed?.ReqFrom ?? parsed?.reqFrom ?? '')
        .trim()
        .toLowerCase();

      if (reqFrom && studentId > 0) {
        return { reqFrom, studentId };
      }
    } catch {
      // ignore malformed reportparams and continue with fallback flow
    }

    return null;
  }

  private getAdminContextFromReturnUrl(): { reqFrom: string; studentId: number } | null {
    const returnUrl = this.route.snapshot.queryParamMap.get('returnUrl');
    if (!returnUrl) {
      return null;
    }

    try {
      const url = new URL(returnUrl, window.location.origin);
      const reportparams = url.searchParams.get('reportparams');
      return this.getAdminContextFromReportParams(reportparams);
    } catch {
      return null;
    }
  }

  private getAdminContextFromTimeoutQuery(): { reqFrom: string; studentId: number } | null {
    const reqFrom = String(this.route.snapshot.queryParamMap.get('reqFrom') ?? '')
      .trim()
      .toLowerCase();
    const studentIdRaw = String(
      this.route.snapshot.queryParamMap.get('studentId') ?? '',
    ).trim();
    const studentIdMatch = studentIdRaw.match(/\d+/);
    const studentId = studentIdMatch ? Number(studentIdMatch[0]) : NaN;

    if (reqFrom && studentId > 0) {
      return { reqFrom, studentId };
    }

    return null;
  }

  private resetAndNavigate(): void {
    const sessionReqFrom = String(sessionStorage.getItem('ReqFrom') ?? '')
      .trim()
      .toLowerCase();
    const sessionStudentId = Number(sessionStorage.getItem('studentId'));
    const queryContext = this.getAdminContextFromTimeoutQuery();
    const fallbackContext = queryContext ?? this.getAdminContextFromReturnUrl();

    const reqFrom = sessionReqFrom || fallbackContext?.reqFrom || '';
    const resolvedStudentId =
      sessionStudentId > 0 ? sessionStudentId : (fallbackContext?.studentId ?? NaN);
    const shouldReturnToAdminPreview = reqFrom === 'admin' && resolvedStudentId > 0;

    this.authService.logout();
    this.sessionService.clearAdmissionStateKeepDomainContext();
    sessionStorage.removeItem('ReqFrom');
    sessionStorage.removeItem('studentId');
    sessionStorage.removeItem('admissionData');
    sessionStorage.removeItem('admissionPayload');
    sessionStorage.removeItem('menuStepCompleted');
    this.domainService.resetDomainCache();

    if (shouldReturnToAdminPreview) {
      this.router.navigate(['/admission/menu'], {
        queryParams: {
          reportparams: this.buildAdminReportParams(resolvedStudentId),
        },
        replaceUrl: true,
      });
      return;
    }

    this.router.navigate(['/admission/student-form'], { replaceUrl: true });
  }

  retrySession(): void {
    this.resetAndNavigate();
  }
}
